/* ─────────────────────────────────────────────────────────────────
   PortalAppointmentsSection
   Shows the client's upcoming (and recent) appointments in the portal.
   Only rendered when the `appointments` module is active.
───────────────────────────────────────────────────────────────── */

export interface PortalAppointment {
  id: string;
  title: string;
  appointment_type: string | null;
  status: string;
  start_at: string;
  end_at: string | null;
  location: string | null;
}

interface Props {
  appointments: PortalAppointment[];
  brandColor?: string;
}

/* ─── Helpers ─────────────────────────────────────────────────── */
const STATUS_STYLES: Record<string, { label: string; bg: string; text: string; dot: string }> = {
  scheduled: { label: "Scheduled", bg: "bg-sky-50",     text: "text-sky-600",     dot: "bg-sky-400" },
  confirmed: { label: "Confirmed", bg: "bg-emerald-50", text: "text-emerald-600", dot: "bg-emerald-400" },
  completed: { label: "Completed", bg: "bg-slate-100",  text: "text-slate-500",   dot: "bg-slate-400" },
  cancelled: { label: "Cancelled", bg: "bg-red-50",     text: "text-red-500",     dot: "bg-red-400" },
  no_show:   { label: "No Show",   bg: "bg-orange-50",  text: "text-orange-500",  dot: "bg-orange-400" },
};

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", {
    weekday: "short",
    month:   "short",
    day:     "numeric",
    year:    "numeric",
  });
}

function fmtTime(iso: string) {
  return new Date(iso).toLocaleTimeString("en-US", {
    hour:   "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function calcDuration(start: string, end: string | null): string | null {
  if (!end) return null;
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms <= 0) return null;
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function isUpcoming(appt: PortalAppointment) {
  return new Date(appt.start_at) >= new Date() && appt.status !== "cancelled" && appt.status !== "no_show";
}

/* ─── Component ──────────────────────────────────────────────── */
export default function AppointmentsSection({ appointments, brandColor = "#C8A96E" }: Props) {
  const upcoming = appointments.filter(isUpcoming);
  const past     = appointments.filter((a) => !isUpcoming(a));

  if (appointments.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5 md:p-6">
        <h2 className="text-[#0f172a] font-semibold text-base mb-4 flex items-center gap-2">
          <span className="w-5 h-5 flex items-center justify-center" style={{ color: brandColor }}>
            <i className="ri-calendar-event-line"></i>
          </span>
          Appointments
        </h2>
        <div className="text-center py-8">
          <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
            <i className="ri-calendar-2-line text-xl text-slate-400"></i>
          </div>
          <p className="text-slate-500 text-sm">No appointments scheduled yet.</p>
          <p className="text-slate-400 text-xs mt-1">
            Your photographer will schedule sessions here when ready.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 md:p-6">
      {/* Section header */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <h2 className="text-[#0f172a] font-semibold text-base flex items-center gap-2">
          <span className="w-5 h-5 flex items-center justify-center" style={{ color: brandColor }}>
            <i className="ri-calendar-event-line"></i>
          </span>
          Appointments
        </h2>
        {upcoming.length > 0 && (
          <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-600 text-xs font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block"></span>
            {upcoming.length} upcoming
          </span>
        )}
      </div>

      <div className="space-y-3">
        {/* Upcoming first */}
        {upcoming.map((appt) => (
          <AppointmentCard key={appt.id} appt={appt} highlight brandColor={brandColor} />
        ))}

        {/* Divider */}
        {upcoming.length > 0 && past.length > 0 && (
          <div className="flex items-center gap-3 py-1">
            <div className="flex-1 border-t border-slate-100"></div>
            <span className="text-slate-300 text-xs">Past</span>
            <div className="flex-1 border-t border-slate-100"></div>
          </div>
        )}

        {/* Past appointments */}
        {past.map((appt) => (
          <AppointmentCard key={appt.id} appt={appt} highlight={false} brandColor={brandColor} />
        ))}
      </div>
    </div>
  );
}

/* ─── Single appointment card ────────────────────────────────── */
function AppointmentCard({ appt, highlight, brandColor = "#C8A96E" }: { appt: PortalAppointment; highlight: boolean; brandColor?: string }) {
  const style    = STATUS_STYLES[appt.status] ?? STATUS_STYLES.scheduled;
  const duration = calcDuration(appt.start_at, appt.end_at);
  const dateStr  = fmtDate(appt.start_at);
  const timeStr  = fmtTime(appt.start_at);

  return (
    <div
      className={`flex items-start gap-4 p-4 rounded-xl border transition-all ${
        highlight
          ? "border-[#C8A96E]/25 bg-[#C8A96E]/5"
          : "border-slate-100 bg-slate-50/50"
      }`}
    >
      {/* Date block */}
      <div className={`shrink-0 w-12 text-center rounded-lg py-1.5 ${highlight ? "bg-[#C8A96E]/15" : "bg-slate-100"}`}>
        <p className={`text-xs font-semibold uppercase tracking-wider leading-none mb-0.5 ${highlight ? "text-[#C8A96E]" : "text-slate-400"}`}>
          {new Date(appt.start_at).toLocaleDateString("en-US", { month: "short" })}
        </p>
        <p className={`text-xl font-bold leading-none ${highlight ? "text-[#C8A96E]" : "text-slate-400"}`}>
          {new Date(appt.start_at).getDate()}
        </p>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <p className={`font-semibold text-sm truncate ${highlight ? "text-[#0f172a]" : "text-slate-500"}`}>
            {appt.title}
          </p>
          {/* Status badge */}
          <span
            className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${style.bg} ${style.text}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full inline-block ${style.dot}`}></span>
            {style.label}
          </span>
        </div>

        <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-1.5">
          {/* Time */}
          <span className="flex items-center gap-1 text-slate-400 text-xs">
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-time-line text-xs"></i>
            </span>
            {dateStr} · {timeStr}
          </span>

          {/* Duration */}
          {duration && (
            <span className="flex items-center gap-1 text-slate-400 text-xs">
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-timer-line text-xs"></i>
              </span>
              {duration}
            </span>
          )}

          {/* Location */}
          {appt.location && (
            <span className="flex items-center gap-1 text-slate-400 text-xs">
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-map-pin-line text-xs"></i>
              </span>
              <span className="truncate max-w-[160px]">{appt.location}</span>
            </span>
          )}

          {/* Type pill */}
          {appt.appointment_type && (
            <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[10px] font-medium">
              {appt.appointment_type}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}